<script setup lang="ts">
import { useQuery } from "@tanstack/vue-query";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import Icon from "@/design/primitives/Icon.vue";
import Skeleton from "@/design/primitives/Skeleton.vue";
import { formatDate, formatDateTime } from "@/shared/format";
import { type AgentSession, searchSessions, useDeleteSession } from "../api/use-agents";
import { useAgentsI18n } from "../i18n/use-agents-i18n";
import { daysAgo, groupSessions } from "./session-groups";

/**
 * As conversas que já existem, com o que o servidor sempre mandou.
 *
 * `updatedAt` e `subject` chegam em toda sessão desde a migração 0056 e a lista
 * os jogava fora: desenhava título e contagem de mensagens, que é o par que menos
 * ajuda a reencontrar uma conversa. Quando foi e sobre o que era é o par que
 * ajuda — e nada disso custa um pedido novo.
 *
 * O distintivo de CONVERSA importa porque as threads ancoradas (uma por
 * conversa de cliente) e as do painel estão na mesma lista, e sem ele "Sem
 * título" aparece quatro vezes seguidas sem nada que as separe.
 *
 * ── AS DUAS FORMAS: `fill` NA DOCA, FAIXA NA TELA DE AGENTES ─────────────
 *
 * Na doca a lista era uma faixa de 14rem por cima do chat, com filtro só a partir
 * de oito — o dono comparou com a V2 e a V2 ganhava: lá o histórico TOMA a doca,
 * a busca está sempre lá com o cursor dentro, e cada linha diz quando foi. Com
 * `fill` é isso: ocupa o pai, busca sempre visível e focada ao abrir, grupos por
 * dia. Sem `fill` continua a faixa compacta da tela de Agentes, onde roubar o
 * foco da página ao carregar seria um defeito e não um conforto.
 *
 * Um componente e não um `<ul>` na doca porque a mesma lista faltava na tela de
 * Agentes, que guardava uma sessão por agente no localStorage e não oferecia
 * jeito nenhum de voltar a uma conversa anterior.
 */
const props = withDefaults(
	defineProps<{
		sessions: readonly AgentSession[];
		selected?: string | null;
		/** Quanto a lista pode ocupar antes de rolar. A doca é estreita; a página não. */
		maxHeight?: string;
		/** Ocupa o pai inteiro: busca sempre visível e focada, grupos por dia (a doca). */
		fill?: boolean;
		autofocus?: boolean;
		agentCode?: string;
		/** A lista ainda não chegou do servidor: esqueleto, e não "nenhuma conversa". */
		loading?: boolean;
	}>(),
	{ autofocus: true },
);

const emit = defineEmits<{ select: [string]; removed: [string] }>();

const { t, ago } = useAgentsI18n();

/**
 * The filter box only from eight on — in the compact strip.
 *
 * Below that the whole list fits in view and a filter is a row of height charging
 * a click to do what the eyes already do. Filling the dock it is always there:
 * it is the first thing the V2 panel offered, and the cursor is already in it.
 */
const FILTER_FROM = 8;

const term = ref("");
const remoteTerm = ref("");
let searchTimer: ReturnType<typeof setTimeout> | undefined;
watch(term, (value) => {
	clearTimeout(searchTimer);
	if (!value.trim()) remoteTerm.value = "";
	else
		searchTimer = setTimeout(() => {
			remoteTerm.value = value.trim().slice(0, 200);
		}, 250);
});
onBeforeUnmount(() => clearTimeout(searchTimer));
const results = useQuery({
	queryKey: computed(() => ["agent-sessions", "search", props.agentCode, remoteTerm.value]),
	enabled: computed(() => !!props.agentCode && !!remoteTerm.value),
	queryFn: ({ signal }) => searchSessions(remoteTerm.value, props.agentCode ?? "", signal),
});
const showFilter = computed(() => props.fill || props.sessions.length >= FILTER_FROM);

/** Sem acento e sem caixa: quem procura "orcamento" quer achar "Orçamento". */
function fold(text: string): string {
	return text
		.normalize("NFD")
		.replace(/\p{Diacritic}/gu, "")
		.toLowerCase();
}

const shown = computed(() => {
	if (props.agentCode && remoteTerm.value) return results.data.value ?? [];
	const needle = fold(term.value.trim());
	if (!needle) return props.sessions;
	return props.sessions.filter((s) => fold(s.title ?? "").includes(needle));
});

/** Na faixa, um grupo só e sem rótulo — a ordem do servidor, como sempre foi. */
const groups = computed(() =>
	props.fill ? groupSessions(shown.value) : [{ group: null, items: [...shown.value] }],
);

/**
 * "3 h" até uma semana, e depois a DATA. "45 d" obriga a fazer a conta para saber
 * que foi em agosto; "12/08" não. O `title` leva a data e a hora inteiras.
 */
function when(iso: string): string {
	return daysAgo(iso, new Date()) < 7 ? ago(iso) : formatDate(iso);
}

const search = ref<HTMLInputElement | null>(null);
onMounted(() => {
	if (props.fill && props.autofocus !== false) search.value?.focus();
});

/**
 * The conversation waiting for a second click before it is deleted.
 *
 * Two clicks and not a `confirm()`, which is blocked in an embedded view and reads
 * as a browser warning rather than as a decision about this conversation.
 */
const removing = ref<string | null>(null);
const deleteSession = useDeleteSession();

async function remove(id: string): Promise<void> {
	if (removing.value !== id) {
		removing.value = id;
		return;
	}
	await deleteSession.mutateAsync(id);
	removing.value = null;
	emit("removed", id);
}
</script>

<template>
	<div class="flex min-h-0 flex-col" :class="fill && 'h-full'">
		<p v-if="agentCode && remoteTerm && results.isError.value" role="alert" class="px-3 text-xs text-danger">{{ t("sessions.searchFailed") }}</p>
		<div v-if="showFilter" class="shrink-0" :class="fill ? 'px-3 pb-2' : 'border-b border-border px-3 py-1.5'">
			<label class="relative block">
				<Icon
					name="search"
					class="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-fg-subtle"
					:class="fill ? 'size-4' : 'size-3.5'"
				/>
				<input
				maxlength="200"
					ref="search"
					v-model="term"
					type="search"
					class="w-full rounded-md border border-border bg-bg-muted text-fg placeholder:text-fg-subtle focus:border-accent focus:bg-surface focus:outline-none"
					:class="fill ? 'py-2 pr-3 pl-8 text-sm' : 'py-1 pr-2 pl-7 text-xs'"
					:placeholder="t('sessions.filter')"
					:aria-label="t('sessions.filter')"
				/>
			</label>
		</div>

		<Skeleton v-if="loading || (agentCode && remoteTerm && results.isFetching.value)" shape="rows" :rows="5" class="px-3 py-2" />

		<ul
			v-else
			class="min-h-0 overflow-y-auto"
			:class="fill && 'flex-1 px-1.5 pb-2'"
			:style="fill ? undefined : { maxHeight: maxHeight ?? '14rem' }"
		>
			<li
				v-if="shown.length === 0 && !term.trim()"
				:class="fill ? 'flex flex-col items-center gap-2 px-4 py-12 text-center text-sm' : 'px-4 py-3 text-xs'"
				class="text-fg-subtle"
			>
				<Icon v-if="fill" name="history" class="size-8 opacity-50" />
				{{ t("sessions.empty") }}
			</li>
			<li
				v-else-if="shown.length === 0 && !results.isError.value"
				:class="fill ? 'flex flex-col items-center gap-2 px-4 py-12 text-center text-sm' : 'px-4 py-3 text-xs'"
				class="text-fg-subtle"
			>
				<Icon v-if="fill" name="search" class="size-8 opacity-50" />
				{{ t("sessions.noMatch", { term }) }}
			</li>
			<template v-for="block in groups" :key="block.group ?? 'all'">
				<li
					v-if="block.group"
					class="sticky top-0 z-10 bg-surface px-2.5 pt-3 pb-1 text-3xs font-semibold tracking-wide text-fg-subtle uppercase"
				>
					{{ t(`sessions.groups.${block.group}`) }}
				</li>
				<li
					v-for="item in block.items"
					:key="item.id"
					class="group flex items-center gap-1"
					:class="fill ? 'rounded-md pr-1.5' : 'pr-2'"
				>
					<button
						type="button"
						class="min-w-0 flex-1 text-left hover:bg-bg-muted"
						:class="[
							fill ? 'rounded-md px-2.5 py-2' : 'px-4 py-2 text-xs',
							selected === item.id ? 'bg-accent-subtle text-accent' : fill ? 'text-fg' : 'text-fg-muted',
						]"
						:aria-current="selected === item.id ? 'true' : undefined"
						:title="formatDateTime(item.updatedAt)"
						@click="emit('select', item.id)"
					>
						<span class="flex items-baseline gap-1.5">
							<!-- Sobre uma CONVERSA de cliente, e não sobre o painel. Sem isto as
							     duas origens se confundem na mesma lista. -->
							<span
								v-if="item.subject?.kind === 'conversation'"
								class="shrink-0 rounded bg-bg-muted px-1 text-3xs text-fg-subtle"
								:title="t('sessions.aboutConversation')"
							>
								{{ t("sessions.conversationBadge") }}
							</span>
							<span class="min-w-0 flex-1 truncate" :class="fill && 'text-sm font-medium'">
								{{ item.title ?? t("sessions.untitled") }}
							</span>
							<span v-if="!fill" class="shrink-0 text-3xs text-fg-subtle">{{ ago(item.updatedAt) }}</span>
						</span>
						<span class="mt-0.5 flex items-center gap-1 text-fg-subtle" :class="fill ? 'text-2xs' : 'text-3xs'">
							<span>{{ t("sessions.messages", { n: item.messageCount }) }}</span>
							<template v-if="fill">
								<span aria-hidden="true">·</span>
								<span>{{ when(item.updatedAt) }}</span>
							</template>
						</span>
					</button>
					<!-- Na doca o X aparece ao passar o rato ou focar a linha, como na V2;
					     armado, fica visível e diz o que o segundo clique faz. -->
					<button
						type="button"
						class="shrink-0 rounded"
						:class="[
							fill && removing !== item.id
								? 'inline-flex size-7 items-center justify-center text-fg-subtle opacity-0 group-hover:opacity-100 hover:bg-danger-subtle hover:text-danger focus-visible:opacity-100 pointer-coarse:opacity-100'
								: 'text-3xs',
							removing === item.id ? 'px-1.5 py-1 font-medium text-danger' : !fill && 'text-fg-subtle hover:text-danger',
						]"
						:aria-label="t('sessions.removeLabel', { title: item.title ?? t('sessions.thisConversation') })"
						@click.stop="remove(item.id)"
					>
						<Icon v-if="fill && removing !== item.id" name="close" class="size-3.5" />
						<template v-else>
							{{ removing === item.id ? t("sessions.confirmRemove") : t("sessions.remove") }}
						</template>
					</button>
				</li>
			</template>
		</ul>
	</div>
</template>
